import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { type NavLayout } from '@/tokens/breakpoints';

/** Keys of the `nav` namespace usable as a navigation label. */
export type NavLabelKey =
  | 'dashboard'
  | 'library'
  | 'sessions'
  | 'store'
  | 'community'
  | 'wishlist'
  | 'family';

export interface NavItem {
  to: string;
  labelKey: NavLabelKey;
  icon: LucideIcon;
  /** The structures where the entry appears at the first level. */
  layouts: readonly NavLayout[];
  /**
   * The entry only exists for someone who belongs to a household.
   *
   * A feature that appears when switched on beats a permanent entry leading
   * nowhere.
   */
  requiresFamily?: true;
  /**
   * Kept out of the navigation entirely, on every structure.
   *
   * For a screen that exists but is not ready to be found — the route still
   * answers, so a link or a bookmark works, and `findNavItem` still names it.
   * Hiding an entry is not forbidding an address.
   *
   * Warning: filtered with `requiresFamily`, **before** the structural pass.
   * Dropping it by emptying `layouts` instead would send it to the overflow
   * drawer, which collects everything the structure leaves out — the opposite
   * of hidden.
   */
  hidden?: true;
}

export interface AppNavProps {
  /** `username` builds the profile URL (`/u/:username`). */
  user: { username: string; displayName: string; avatarUrl: string | null };
  /**
   * Counters injected by the application layer.
   *
   * Warning: `AppNav` lives in `components/` and cannot query a feature — lint
   * forbids it — which is what keeps it pure and testable without a backend.
   */
  unread: { notifications: number; messages: number };
  /**
   * Whether the reader belongs to a household.
   *
   * Injected by the application layer, for the same reason as `unread`.
   */
  hasFamily: boolean;
  onScan: () => void;
  /**
   * A bubble anchored above the scan button, on the narrow structure only.
   *
   * Passed in rather than decided here: whether it is worth showing depends on
   * what the account holds, and this component may not read a feature. Its
   * presence also makes the button pulse — the bubble names the gesture, the
   * halo points at it.
   */
  scanHint?: ReactNode;
  /**
   * Opens the notifications drop-down instead of following the link.
   *
   * Warning: absent on narrow formats, where the bell goes straight to the
   * page — a panel anchored to a 375 px viewport is the page, minus the room
   * to read it.
   */
  onOpenNotifications?: () => void;
  /**
   * The drop-down itself, when it is open.
   *
   * Passed in rather than rendered here: it reads the notifications feature,
   * and this component may not — which is what keeps it testable without a
   * backend.
   */
  notificationsPanel?: ReactNode;
  /** Forces a structure; for Storybook and tests only. */
  forceLayout?: NavLayout;
  className?: string;
}

export interface AppNavPresenterProps extends Omit<AppNavProps, 'forceLayout'> {
  items: NavItem[];
  /** Entries this structure leaves out, destined for the drawer. */
  overflowItems: NavItem[];
}
